import { AppApi } from '@/customApiClients/appApiClient/Api';
import {
    ManagedAgentResponseModel,
    MgmtApi
} from '@/customApiClients/mgmtApiClient/Api';
import { useEffect, useMemo } from 'react';
import { useRecoilState } from 'recoil';
import { toast } from 'sonner';

import { useChatInteract } from '@chainlit/react-client';

import {
    ManagedAgentDbConfig,
    setMultiAgentRoster
} from '@/components/chat/MessageComposer/multiAgentActivation';

import {
    accessibleManagedAgentsLoadingState,
    accessibleManagedAgentsState,
    agentRosterSavingState,
    savedAgentRosterCheckedState,
    selectedSubAgentsState
} from '@/state/chat';

/** The user's sub-agent roster, shared between the composer's MultiAgentSelector and the sidebar's AgentsPanel. */
let savedSelectionRestoreStarted = false;
let agentListFetchStarted = false;
let autoDefaultApplied = false;
// Chains persist() calls so concurrent toggles reach the backend in client-side order.
let pendingPersist: Promise<void> = Promise.resolve();
let pendingPersistCount = 0;

export function useAgentRoster() {
    const mgmtApi = useMemo(
        () =>
            new MgmtApi({
                baseUrl: '/mgmt',
                baseApiParams: { credentials: 'include' }
            }),
        []
    );
    const appApi = useMemo(
        () => new AppApi({ baseApiParams: { credentials: 'include' } }),
        []
    );

    const [selected, setSelected] = useRecoilState(selectedSubAgentsState);
    const [agents, setAgents] = useRecoilState(accessibleManagedAgentsState);
    const [loading, setLoading] = useRecoilState(
        accessibleManagedAgentsLoadingState
    );
    const [saving, setSaving] = useRecoilState(agentRosterSavingState);
    const [hasCheckedSavedSelection, setHasCheckedSavedSelection] =
        useRecoilState(savedAgentRosterCheckedState);
    const { windowMessage } = useChatInteract();

    useEffect(() => {
        if (savedSelectionRestoreStarted) return;
        savedSelectionRestoreStarted = true;

        appApi.agent
            .getAgentOnUser()
            .then((res) => {
                const saved = res?.data;
                if (
                    saved?.Model === 'managed-agent' &&
                    Array.isArray(saved.SubAgentRoster) &&
                    saved.SubAgentRoster.length > 0
                ) {
                    setSelected(saved.SubAgentRoster);
                } else {
                    setSelected([]);
                }
            })
            .catch(() => {})
            .finally(() => setHasCheckedSavedSelection(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (agentListFetchStarted) return;
        agentListFetchStarted = true;

        setLoading(true);
        mgmtApi.api
            .v1ManagedAgentsAccessibleList()
            .then((res) => {
                const list = (res?.data?.data || []).filter(
                    (a) => !a.isCoordinator
                );
                setAgents(list);
                setSelected((sel) =>
                    sel.filter((id) => list.some((a) => a.agentId === id))
                );
            })
            .catch(() => {
                agentListFetchStarted = false;
                setAgents([]);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mgmtApi]);

    const resolveDisplayName = (roster: string[]): string => {
        if (roster.length === 1) {
            const only = agents?.find((a) => a.agentId === roster[0]);
            return only?.displayName || 'Agent';
        }
        return `Agents ${roster.length}`;
    };

    const persist = (roster: string[]) => {
        if (roster.length === 0) return;
        const primary = agents?.find((a) => a.agentId === roster[0]);
        if (!primary) return;
        const dbConfig: ManagedAgentDbConfig = {
            organization: primary.organization,
            environmentId: primary.environmentId,
            vaultId: primary.vaultId,
            apiKeySecret: primary.apiKey,
            version: primary.version,
            enableRls: primary.enableRls
        };

        const run = async () => {
            try {
                await setMultiAgentRoster(
                    roster,
                    resolveDisplayName(roster),
                    dbConfig,
                    appApi
                );
                windowMessage({ type: 'agent_changed' });
            } catch (e: any) {
                toast.error(e?.message || 'Kunne ikke gemme agent-valget');
            } finally {
                pendingPersistCount -= 1;
                if (pendingPersistCount === 0) setSaving(false);
            }
        };

        pendingPersistCount += 1;
        setSaving(true);
        pendingPersist = pendingPersist.then(run);
    };

    useEffect(() => {
        if (
            hasCheckedSavedSelection &&
            !loading &&
            !autoDefaultApplied &&
            selected.length === 0 &&
            agents?.length === 1
        ) {
            autoDefaultApplied = true;
            const onlyAgentId = agents[0].agentId;
            setSelected([onlyAgentId]);
            void persist([onlyAgentId]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasCheckedSavedSelection, loading, selected, agents]);

    const toggle = (agentId: string) => {
        const next = selected.includes(agentId)
            ? selected.filter((x) => x !== agentId)
            : [...selected, agentId];
        setSelected(next);
        void persist(next);
    };

    const pickableAgents: ManagedAgentResponseModel[] = agents ?? [];

    return {
        agents: pickableAgents,
        agentCount: agents?.length,
        selected,
        toggle,
        loading,
        saving,
        hasCheckedSavedSelection,
        resolveDisplayName
    };
}
